import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, merge, of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    startWith,
    switchMap,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import { CopilotSidePanelService } from './copilot-side-panel.service';
import { CopilotChatController } from './copilot-chat-controller';
import { PAGE_CONTEXT_FORMAT_NOTE, PageContextService } from './page-context.service';

/** Request body for the backchatty Ask endpoint (chatservice AskRequest). */
interface AskRequest {
    query: string;
}

/** Response body for the blocking Ask endpoint (chatservice AskResponse). */
interface AskResponse {
    response: string;
}

export type PageSummaryStateName = 'idle' | 'loading' | 'ready' | 'error';

export interface PageSummaryState {
    state: PageSummaryStateName;
    /** Markdown summary text; '' unless `state === 'ready'`. */
    summary: string;
}

/**
 * Prompt used to summarize a page that has no page-specific `copilotPageSummaryPrompt`
 * in its route data. Grounded, no page/UI-describing meta language, and format-constrained
 * so it reads consistently on any page (the per-page prompts follow the same contract).
 */
export const DEFAULT_SUMMARY_PROMPT =
    'Summarize what matters to you on this view, using ONLY the data shown above — never invent array names, metrics, or values. Write about your fleet and what to do, not about the page: never say "This page shows/displays…" or "It includes…", and don\'t list field or column names. Lead with anything that needs attention (warnings, errors, outliers, or items nearing a limit); if nothing stands out, say things look healthy for what’s shown. Start with a bold "Key Highlights" heading, then at most 3 bullets, each citing a concrete item or value. If little or no data is available, say exactly that in one short sentence. Under 100 words, no preamble.';

/**
 * Shared voice directive appended to EVERY summary query — the per-page prompts and the
 * default alike — so summaries always address the customer directly in the second person
 * ("you"/"your") and never lapse into third person ("the customer", "the user"). Centralized
 * here rather than repeated in each prompt so the voice applies uniformly and can't drift as
 * new per-page prompts are added.
 */
export const SECOND_PERSON_INSTRUCTION =
    ' Address the reader directly in the second person — use "you" and "your" (e.g. "You have 4 subscriptions"), and never refer to "the customer", "the user", or the organization in the third person.';

/**
 * Shared grounding directive appended to EVERY summary query, targeting the misread-numbers
 * failure mode (CLOUD-231829): values must be quoted exactly as shown and attributed to the
 * label/column they appear under, quoting beats computing, and the overall verdict must not
 * contradict the cited values (observed live: "96% utilization" bullet followed by "no
 * arrays close to full").
 */
export const NUMERIC_GROUNDING_INSTRUCTION =
    ' When citing numbers, use the exact values shown and attribute each to the label or column it appears under; prefer quoting values as shown over computing new ones. Keep your overall assessment consistent with the values you cite — never describe things as healthy while also citing a value that needs attention.';

/**
 * Shared calibration directive appended to EVERY summary query (CLOUD-239236): a sub-90%
 * array was flagged "high capacity utilization", and "High IOPS at 73.0 K" was flagged
 * despite proportionally higher bandwidth beside it. Below 90%, capacity may only be described
 * as the highest/lowest shown, never "high"; IOPS/bandwidth/data-reduction ratio have no
 * threshold at all and may never be characterized as high/low/strong/weak, including via
 * synonyms like "exceptionally". Banning the words outright, rather than asking the model to
 * compare values itself, is what held up under empirical testing (see PR for the iteration
 * log). Centralized alongside the two directives above so calibration can't drift per page.
 */
export const HIGHLIGHT_CALIBRATION_INSTRUCTION =
    ' Calibrate what you flag. Treat capacity at or above 90% used as near full and flag it as needing attention; describe anything below 90% only as the highest or lowest utilization among those shown, and never as high, elevated, near full, or nearly out of space, however it ranks. IOPS, bandwidth, and data-reduction ratio have no threshold at all, not a fleet-wide one and not a general one, so no value for them is ever high, low, strong, or weak, no matter how large or small the number looks by itself. Never use those words, or words like notably, exceptionally, unusually, or significant, to characterize an IOPS, bandwidth, or data-reduction figure. State the exact value; you may add that it is the highest or lowest among those shown if that is true, and nothing more. Do not flag one metric as elevated without checking the related metrics shown beside it for that same record. When choosing which of your bullets to include, an appliance meeting the 90%-or-above condition above always takes one of your at most three, regardless of what other metrics might otherwise seem more attention-grabbing. Include a bullet only if it is genuinely notable: one accurate highlight beats three, and padding the list with a non-issue is a failure.';

const TRIGGER_DEBOUNCE_MS = 150;
/** Blocking backchatty Ask endpoint (via the staging Kong `backchatty-ask` route). */
const ENDPOINT = '/api/1.0/chat/ask';
/** Ceiling for a single Ask request, so a hung backend can't leave the skeleton up forever. */
const REQUEST_TIMEOUT_MS = 30_000;
/** Backend cap on the AskRequest `query` field (chars), per chat_service.proto / ask.py MAX_QUERY_LENGTH. */
const MAX_QUERY_LENGTH = 100_000;
/** Keep the query safely under the backend cap (95%); the page content is trimmed to fit if needed. */
const QUERY_CHAR_BUDGET = Math.floor(MAX_QUERY_LENGTH * 0.95);
/** Marker appended when page content is trimmed to fit the query budget. */
const TRUNCATION_NOTE = '\n…[page content truncated to fit length limit]';
/** Route-data key carrying a page-specific summary prompt. */
const ROUTE_PROMPT_KEY = 'copilotPageSummaryPrompt';
/**
 * Route-data key that opts a page out of the summary. Opt-out (unlike `copilotPageSuggestions`):
 * every page gets a summary unless it sets this to `false` — for pages whose content the page
 * context cannot capture, such as the Workflows views rendered inside a Workato iframe.
 */
const ROUTE_ENABLED_KEY = 'copilotPageSummary';

/**
 * Builds the Ask `query`: the page context (URL + stripped text/table rows) followed by the
 * page's summary instruction and the shared voice + numeric-grounding directives. The task
 * lives in the query (not `system_instruction`, which is dev-only and may be ignored in
 * prod). The page content is trimmed if needed so the whole query stays within
 * QUERY_CHAR_BUDGET; the framing and instructions are always preserved.
 */
function buildPageSummaryQuery(prompt: string, url: string, html: string): string {
    const intro =
        'You are assisting a user of the Pure1 fleet-management console.\n' +
        `They are currently viewing this page (URL: ${url}). Its visible content follows. ` +
        `${PAGE_CONTEXT_FORMAT_NOTE}\n\n`;
    const outro = `\n\nBased on the specific data shown above: ${prompt}${SECOND_PERSON_INSTRUCTION}${NUMERIC_GROUNDING_INSTRUCTION}${HIGHLIGHT_CALIBRATION_INSTRUCTION}`;

    const htmlBudget = Math.max(0, QUERY_CHAR_BUDGET - intro.length - outro.length - TRUNCATION_NOTE.length);
    const body = html.length > htmlBudget ? html.slice(0, htmlBudget) + TRUNCATION_NOTE : html;
    return intro + body + outro;
}

/**
 * Generates a page summary shown in the Copilot side panel in place of the empty-state
 * prompt. It fires the same trigger as page-suggested questions — the panel being open
 * on an empty conversation (including after "new chat" or a navigation while shown) — and
 * hits the same blocking Ask endpoint with the same page context; only the query differs.
 * The query is built from the current route's `copilotPageSummaryPrompt` (or the shared
 * default) so each page can summarize itself in its own terms.
 */
@Injectable({ providedIn: 'root' })
export class CopilotPageSummaryService implements OnDestroy {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly sidePanel = inject(CopilotSidePanelService);
    private readonly controller = inject(CopilotChatController);
    private readonly pageContext = inject(PageContextService);

    private readonly stateSubject = new BehaviorSubject<PageSummaryState>({
        state: 'idle',
        summary: '',
    });
    readonly state$: Observable<PageSummaryState> = this.stateSubject.asObservable();

    /** Fired by `refresh()` to bypass the trigger pipeline and force a new summary. */
    private readonly refreshSubject = new Subject<void>();

    private readonly destroy$ = new Subject<void>();
    private subscription: Subscription | null = null;
    private currentAbort: AbortController | null = null;
    /** Per-request timeout ceiling (overridable in tests). */
    private readonly requestTimeoutMs = REQUEST_TIMEOUT_MS;

    /** Wires up the trigger pipeline. Call once during app bootstrap (gated by the flag). */
    start(): void {
        if (this.subscription) {
            return;
        }
        const url$ = this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(e => e.urlAfterRedirects.split('?')[0]),
            startWith(this.router.url.split('?')[0]),
            distinctUntilChanged(),
        );

        // The summary is shown only while the panel is open on an empty conversation.
        const shouldShow$ = combineLatest([this.sidePanel.isOpen$, this.controller.conversation$]).pipe(
            map(([open, convo]) => open && (convo?.messages?.length ?? 0) === 0),
            distinctUntilChanged(),
        );

        // Regenerate every time the panel is shown again on an empty conversation — including
        // reopening on the SAME page and starting a new chat (which empties the conversation).
        const shown$ = shouldShow$.pipe(
            filter(shouldShow => shouldShow),
            map(() => undefined),
        );

        // Also regenerate when the route changes while the summary is on screen.
        const urlChangedWhileShown$ = url$.pipe(
            withLatestFrom(shouldShow$),
            filter(([, shouldShow]) => shouldShow),
            map(() => undefined),
        );

        const trigger$ = merge(shown$, urlChangedWhileShown$);

        // Clear any stale summary and show the loading skeleton the instant the panel is
        // shown (or the page changes under it), before the debounce + fetch window, so the
        // previous page's summary never flashes while the new one is being generated.
        //
        // A page that opted out goes straight to idle (the plain placeholder) instead — never
        // flashing a skeleton for a summary that will not arrive — and anything still in flight
        // from the previous page is aborted here rather than landing on this one.
        this.subscription = trigger$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            if (!this.summaryEnabledForCurrentPage()) {
                this.currentAbort?.abort();
                this.currentAbort = null;
                this.stateSubject.next({ state: 'idle', summary: '' });
                return;
            }
            this.stateSubject.next({ state: 'loading', summary: '' });
        });

        // Debounced (re)generation. Re-check shouldShow at fire time: opening with a prompt
        // (or a fast close) can make the conversation non-empty / close the panel during the
        // debounce, and we must not fetch then. refresh() bypasses this to force a re-generate.
        const debouncedFetch$ = trigger$.pipe(
            debounceTime(TRIGGER_DEBOUNCE_MS),
            withLatestFrom(shouldShow$),
            filter(([, shouldShow]) => shouldShow),
        );

        this.subscription.add(
            merge(debouncedFetch$, this.refreshSubject)
                .pipe(
                    takeUntil(this.destroy$),
                    // Routed through switchMap rather than filtered out, so that arriving on an
                    // opted-out page still cancels whatever the previous page left in flight.
                    switchMap(() =>
                        this.summaryEnabledForCurrentPage()
                            ? this.fetchSummary()
                            : of<PageSummaryState>({ state: 'idle', summary: '' }),
                    ),
                )
                .subscribe(state => this.stateSubject.next(state)),
        );

        // Abort any in-flight fetch and reset to idle when the user starts a chat.
        // A separate subscription (not tap+filter on the trigger) is required so that
        // the AbortController fires even when switchMap has no new trigger to switch on.
        // Closing the panel does NOT reset here — reopening re-triggers a fresh fetch above.
        this.subscription.add(
            this.controller.conversation$
                .pipe(
                    filter(convo => (convo?.messages?.length ?? 0) > 0),
                    takeUntil(this.destroy$),
                )
                .subscribe(() => {
                    this.currentAbort?.abort();
                    this.currentAbort = null;
                    this.stateSubject.next({ state: 'idle', summary: '' });
                }),
        );
    }

    stop(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
        this.currentAbort?.abort();
        this.currentAbort = null;
    }

    /**
     * Regenerate the summary for the current page without changing routes or reopening the
     * panel. Bypasses the trigger pipeline (and its debounce) so the user gets a new summary
     * on demand; `fetchSummary()` emits `loading` first, so the skeleton returns immediately,
     * and its `switchMap` abandons any summary still in flight.
     */
    refresh(): void {
        this.refreshSubject.next();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.stop();
    }

    /**
     * Whether the current page wants a summary. The deepest `copilotPageSummary` declaration on
     * the activated path wins, so a child route can opt back in under an opted-out parent;
     * absent (or `true`) means summarize.
     */
    private summaryEnabledForCurrentPage(): boolean {
        let node: ActivatedRouteSnapshot | null = this.router.routerState?.snapshot?.root ?? null;
        let enabled = true;
        while (node) {
            const value = node.data?.[ROUTE_ENABLED_KEY];
            if (typeof value === 'boolean') {
                enabled = value;
            }
            node = node.firstChild;
        }
        return enabled;
    }

    /** Walk the activated route tree and return the deepest `copilotPageSummaryPrompt`, or the default. */
    private currentPrompt(): string {
        let node: ActivatedRouteSnapshot | null = this.router.routerState?.snapshot?.root ?? null;
        let prompt: string | undefined;
        while (node) {
            const value = node.data?.[ROUTE_PROMPT_KEY];
            if (typeof value === 'string' && value.length > 0) {
                prompt = value;
            }
            node = node.firstChild;
        }
        return prompt ?? DEFAULT_SUMMARY_PROMPT;
    }

    /**
     * Fetches the summary via a single blocking call to the backchatty Ask endpoint.
     * Sends the page context + the page's summary prompt as the free-text `query`
     * (no response schema — the summary is prose/markdown). Emits `loading` while in
     * flight, then `ready` (with the summary) or `error`.
     */
    private fetchSummary(): Observable<PageSummaryState> {
        const { url, html } = this.pageContext.captureCurrent();
        const query = buildPageSummaryQuery(this.currentPrompt(), url, html);

        return new Observable<PageSummaryState>(observer => {
            observer.next({ state: 'loading', summary: '' });

            // Cancel any previous in-flight request.
            this.currentAbort?.abort();
            const abort = new AbortController();
            this.currentAbort = abort;

            // Single completion path, guarded so the timeout, the response, and cancellation
            // can't double-emit or emit after complete.
            let finished = false;
            function finish(state?: PageSummaryState): void {
                if (finished) {
                    return;
                }
                finished = true;
                clearTimeout(timeoutId);
                if (state) {
                    observer.next(state);
                }
                observer.complete();
            }

            // Error out (and cancel the request) if the backend never responds within the ceiling.
            const timeoutId = window.setTimeout(() => {
                finish({ state: 'error', summary: '' });
                abort.abort();
            }, this.requestTimeoutMs);

            // Through HttpClient, not fetch, so the request carries the org headers (and XSRF token) the
            // interceptors add. The shared AbortController stays the cancellation handle for chat-start,
            // stop() and opted-out navigation: aborting it cancels the HTTP request and completes with no
            // state. Listening before subscribing keeps that true even if the request settles synchronously.
            const request = new Subscription();
            abort.signal.addEventListener('abort', () => {
                request.unsubscribe();
                finish();
            });
            request.add(
                this.http.post<AskResponse>(ENDPOINT, { query } as AskRequest).subscribe({
                    next: data => {
                        const summary = (data?.response ?? '').trim();
                        finish(summary.length > 0 ? { state: 'ready', summary } : { state: 'error', summary: '' });
                    },
                    // Non-2xx, network failure, or an unparseable body.
                    error: () => finish({ state: 'error', summary: '' }),
                }),
            );

            return () => {
                clearTimeout(timeoutId);
                abort.abort();
                request.unsubscribe();
            };
        });
    }
}
